import { Link } from "react-router-dom";
import {
  ArrowRight,
  Wifi,
  Zap,
  BedDouble,
  Utensils,
  BellRing,
  Soup,
  Landmark,
  Train,
  Plane,
  Cross,
  ShieldCheck,
  Compass,
  Sunrise,
  Car,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/hotel";
import { PROPERTIES } from "@/data/properties";

const STATS = [
  ["1980", "Founded"],
  ["14", "Rooms"],
  ["3★", "Premium"],
  ["24/7", "Front Desk"],
];

const DINING_IMAGES = [
  {
    src: "/images/302b3c74eef7.webp",
    alt: "Indian breakfast at Hotel Azulo Inn — paratha, papad, curd, juice and chai served in-room",
    label: "Indian",
    className: "mt-0",
  },
  {
    src: "/images/948397bda223.webp",
    alt: "Continental breakfast at Hotel Azulo Inn — rusk, butter, jam, orange juice and coffee",
    label: "Continental",
    className: "mt-8",
  },
  {
    src: "/images/5f0710d9e4fb.webp",
    alt: "American breakfast at Hotel Azulo Inn — eggs on toast, cereal, butter and tea on a wooden tray",
    label: "American",
    className: "mt-0",
  },
];

const DINING_FEATURES = [
  {
    icon: Utensils,
    title: "The In-House Restaurant",
    blurb:
      "Our kitchen at Hotel Azulo Inn features an extensive multi-cuisine menu. From comforting North Indian gravies and aromatic tandoori specialties to fresh Continental and Chinese options, every meal is prepared fresh by our chefs.",
  },
  {
    icon: BellRing,
    title: "In-Room Dining · 24/7",
    blurb:
      "Whether you are returning late from a business meeting at Bhikaji Cama Place or resting after a long flight, enjoy delicious, hot meals served straight to the comfort of your room.",
  },
  {
    icon: Soup,
    title: "Custom Dietary Requests",
    blurb:
      "Catering specifically to medical travellers and families, our kitchen is happy to prepare mild, customised meals upon prior request.",
  },
];

const SERVICES = [
  {
    icon: Wifi,
    title: "High-Speed Wi-Fi & Work Desks",
    blurb: "Seamless connectivity with dedicated work desks — ideal for corporate stays and visa preparation.",
  },
  {
    icon: Utensils,
    title: "In-House Kitchen & Fresh Dining",
    blurb: "Continental, Indian & custom meals prepared fresh daily by our in-house kitchen.",
  },
  {
    icon: Zap,
    title: "24/7 Power Backup & Air Conditioning",
    blurb: "Fully air-conditioned spaces with uninterrupted power systems, day and night.",
  },
  {
    icon: Compass,
    title: "Dedicated Concierge & Front Desk",
    blurb: "Airport transfers, embassy appointments, and city guidance arranged in-house, round the clock.",
  },
  {
    icon: Sunrise,
    title: "Early Breakfast Option",
    blurb: "Breakfast starting 6:00 AM for morning Embassy appointments and early departures.",
  },
  {
    icon: Car,
    title: "Safe Gated Enclave with Free Parking",
    blurb: "A quiet, secure residential address with complimentary on-site parking.",
  },
];

const LOCATION_HIGHLIGHTS = [
  { icon: Landmark, label: "10 Mins to US Embassy & Diplomatic Enclave" },
  { icon: Train, label: "650m to Bhikaji Cama Place Metro Station" },
  { icon: Plane, label: "15 Mins to Delhi International Airport (IGI)" },
  { icon: Cross, label: "Near AIIMS & Safdarjung Hospital" },
  { icon: ShieldCheck, label: "Safe & Quiet Residential Enclave" },
];

export const LocationRibbon = () => (
  <section data-testid="location-ribbon" className="bg-ink-900 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {LOCATION_HIGHLIGHTS.map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-sage-light shrink-0" strokeWidth={1.5} />
          <span className="text-[12px] font-medium text-paper-200 leading-snug">{label}</span>
        </div>
      ))}
    </div>
  </section>
);


export const IntroSection = () => (
  <section data-testid="section-intro" className="py-24 md:py-32 bg-paper-50">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-4">
        <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-sage-dark">
          About Hotel Azulo Inn
        </div>
        <div className="mt-6 font-serif italic text-sage-dark text-3xl leading-tight">
          Since 1980.
        </div>
        <div className="mt-3 text-xs font-semibold tracking-[0.22em] uppercase text-ink-500">
          Premium 3-star hospitality · South Delhi
        </div>
      </div>
      <div className="md:col-span-8">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-ink-900">
            Contemporary comfort in the
            <span className="italic text-sage-dark"> heart of South Delhi.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-8 max-w-2xl text-ink-800 text-base md:text-lg leading-[1.75]">
            Hotel Azulo Inn offers premium 3-star hospitality tailored for business
            executives, medical travellers, and families visiting the capital.
            Strategically located at <strong className="font-medium text-ink-900">B1/16,
            Safdarjung Enclave</strong> — a minute&rsquo;s walk from Bhikaji Cama Place metro —
            our fourteen quietly considered rooms combine modern accommodations with warm,
            efficient service to ensure a seamless stay in New Delhi.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 max-w-2xl text-ink-800 text-base md:text-lg leading-[1.75]">
            Well-appointed, spacious rooms with contemporary interiors, high-speed Wi-Fi,
            an elegant in-house restaurant, and a lush garden lawn. Excellent transit
            connectivity to Delhi&rsquo;s major commercial centres, embassies, hospitals, and
            cultural landmarks.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
            {STATS.map(([n, l]) => (
              <div key={l} className="border-l-2 border-sage/50 pl-4">
                <div className="font-serif text-3xl text-ink-900">{n}</div>
                <div className="overline mt-1">{l}</div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.24}>
          <div className="mt-10 flex gap-3 flex-wrap">
            <Link
              to="/book"
              data-testid="intro-book-btn"
              className="inline-flex items-center h-11 px-6 bg-sage text-paper-50 text-[11px] font-semibold tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors"
            >
              Book a Stay
            </Link>
            <Link
              to="/rooms"
              data-testid="intro-rooms-btn"
              className="inline-flex items-center h-11 px-6 border border-ink-900/25 text-[11px] font-semibold tracking-[0.22em] uppercase text-ink-900 hover:bg-ink-900 hover:text-paper-50 transition-colors"
            >
              Explore Rooms
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const PropertiesSection = () => (
  <section
    data-testid="section-properties"
    className="py-24 md:py-32 bg-paper-100 border-y hairline"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
      <div className="overline">Our Hotels</div>
      <h2 className="mt-5 font-serif text-4xl md:text-5xl text-ink-900 leading-[1.05]">
        Distinct addresses,
        <span className="italic text-sage-dark"> signature comfort.</span>
      </h2>
    </div>

    <div className="space-y-16 md:space-y-24">
      {PROPERTIES.map((p, i) => (
        <Reveal key={p.slug} delay={i * 0.05}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className={`md:col-span-7 ${i % 2 ? "md:order-2" : ""}`}>
              <div className="relative aspect-[16/10] overflow-hidden bg-paper-200 group">
                <img
                  src={p.image}
                  alt={`${p.name} — ${p.location}`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="overline">{p.kind}</div>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
                {p.name}
              </h3>
              <p className="mt-4 text-ink-700 leading-relaxed">{p.blurb}</p>
              <div className="mt-6 text-sm text-ink-600">
                <div>{p.location}</div>
                <div className="mt-1">{p.rooms}</div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {p.slug === "hotel-azulo-inn" ? (
                  <>
                    <Link
                      to="/book"
                      data-testid={`property-book-${p.slug}`}
                      className="inline-flex items-center gap-2 h-11 px-6 bg-sage text-paper-50 text-[11px] font-semibold tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors"
                    >
                      Book Now <ArrowRight size={14} />
                    </Link>
                    <Link
                      to="/rooms"
                      data-testid={`property-rooms-${p.slug}`}
                      className="inline-flex items-center h-11 px-6 border border-ink-900/25 text-[11px] font-semibold tracking-[0.22em] uppercase text-ink-900 hover:bg-ink-900 hover:text-paper-50 transition-colors"
                    >
                      View Rooms
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/properties#ambica"
                    data-testid={`property-visit-${p.slug}`}
                    className="inline-flex items-center gap-2 h-11 px-6 border border-ink-900/25 text-[11px] font-semibold tracking-[0.22em] uppercase text-ink-900 hover:bg-ink-900 hover:text-paper-50 transition-colors"
                  >
                    Learn More <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const DiningSection = () => (
  <section data-testid="section-dining" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="overline">Dining at Azulo</div>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl text-ink-900 leading-[1.05]">
            Flavours of the capital,
            <span className="italic text-sage-dark"> served fresh daily.</span>
          </h2>
          <p className="mt-8 max-w-md text-ink-800 text-base md:text-lg leading-[1.75]">
            Start your day in South Delhi with a wholesome, complimentary breakfast
            spread featuring a blend of classic Indian favourites and global options.
          </p>
          <Link
            to="/gallery"
            data-testid="dining-explore-btn"
            className="mt-8 inline-flex items-center h-11 px-6 border border-ink-900/25 text-[11px] tracking-[0.22em] uppercase text-ink-900 hover:bg-ink-900 hover:text-paper-50 transition-colors"
          >
            See the gallery
          </Link>
        </div>
        <div className="md:col-span-7 grid grid-cols-3 gap-3 md:gap-4">
          {DINING_IMAGES.map((img) => (
            <div key={img.label} className={`${img.className} relative aspect-[4/5] overflow-hidden bg-paper-200 group`}>
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-900/70 to-transparent p-4">
                <div className="text-[10px] tracking-[0.24em] uppercase text-paper-50">
                  {img.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {DINING_FEATURES.map(({ icon: Icon, title, blurb }, i) => (
          <Reveal key={title} delay={i * 0.06}>
            <div className="bg-paper-100 border hairline p-8 md:p-10 h-full">
              <div className="w-11 h-11 bg-sage/15 border border-sage/30 flex items-center justify-center">
                <Icon className="w-5 h-5 text-sage-dark" strokeWidth={1.5} />
              </div>
              <h3 className="mt-6 font-serif text-2xl text-ink-900 leading-tight">
                {title}
              </h3>
              <p className="mt-4 text-ink-700 leading-[1.7]">{blurb}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ServicesSection = () => (
  <section data-testid="section-services" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-3xl">
        <div className="overline">Guest Advantages & Amenities</div>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl text-ink-900 leading-[1.05]">
          Engineered for corporate efficiency, medical travel comfort,
          <span className="italic text-sage-dark"> and relaxing family stays.</span>
        </h2>
      </div>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map(({ icon: Icon, title, blurb }, i) => (
          <Reveal key={title} delay={i * 0.05}>
            <div className="bg-paper-100 p-8 md:p-10 h-full border hairline">
              <div className="w-11 h-11 bg-sage/15 border border-sage/30 flex items-center justify-center">
                <Icon className="w-5 h-5 text-sage-dark" strokeWidth={1.5} />
              </div>
              <h3 className="mt-6 font-serif text-2xl text-ink-900 leading-tight">
                {title}
              </h3>
              <p className="mt-4 text-ink-700 leading-[1.7]">{blurb}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const GrowSection = () => (
  <section
    data-testid="section-grow"
    className="py-24 md:py-32 bg-paper-100 border-y hairline"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        <Link
          to="/careers"
          data-testid="grow-careers-card"
          className="group block bg-paper-50 p-10 md:p-14 border hairline hover:border-sage transition-colors"
        >
          <div className="overline">Careers</div>
          <h3 className="mt-4 font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
            Build a career in <span className="italic text-sage-dark">hospitality.</span>
          </h3>
          <p className="mt-5 text-ink-700 leading-relaxed">
            We&rsquo;re hiring across operations, F&amp;B, sales, and guest services. Join a heritage
            group with a modern outlook — and a growing footprint across India.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-sage-dark">
            See careers <ArrowRight size={12} />
          </div>
        </Link>

        <Link
          to="/partner"
          data-testid="grow-partner-card"
          className="group block bg-paper-50 p-10 md:p-14 border hairline hover:border-sage transition-colors"
        >
          <div className="overline">Partner With Us</div>
          <h3 className="mt-4 font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
            Own a property? <span className="italic text-sage-dark">Let&rsquo;s talk.</span>
          </h3>
          <p className="mt-5 text-ink-700 leading-relaxed">
            If you have a property suited to a boutique hotel, serviced apartments, or
            independent residential suites — Azulo can help maximise its potential with
            sales, marketing, and end-to-end management.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-sage-dark">
            Partner with Azulo <ArrowRight size={12} />
          </div>
        </Link>
      </div>
    </div>
  </section>
);

export const TestimonialSection = () => (
  <section data-testid="section-press" className="py-24 md:py-32">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <div className="overline">Guests</div>
      <blockquote className="mt-8 font-serif italic text-3xl md:text-5xl leading-snug text-ink-900">
        &ldquo;{TESTIMONIALS[0].quote}&rdquo;
      </blockquote>
      <div className="mt-8 overline text-ink-500">— {TESTIMONIALS[0].author}</div>
    </div>
  </section>
);
